import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { EnvironmentService, EventService, ListService, fromList } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { MultipleListOfValuesResponse } from '#types/list';

import { CONSTANTS } from '@system/constants';

import * as SystemActions from './system.actions';

@Injectable()
export class SystemEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private environmentService: EnvironmentService,
    private listService: ListService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SystemActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectSystemLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(SystemActions.RequestSystemLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestSystemLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SystemActions.RequestSystemLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('system').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => SystemActions.RequestSystemListsSuccess({ lists })),
          catchError((error) => of(SystemActions.RequestSystemListsFailure(error)))
        );
      })
    );
  });
}
